import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useNavigate, useLocation, Location } from 'react-router-dom';
import { QueryParamProvider, transformSearchStringJsonSafe } from 'use-query-params';
import 'typeface-roboto';

import App from './containers/App/App';
import ThemeSelector from './components/ThemeSelector';
import * as serviceWorker from './serviceWorker';
import './index-hulu.scss';

// Suppress ResizeObserver error from ReactFlow
window.addEventListener('error', e => {
  if (e.message && e.message.includes('ResizeObserver')) {
    const isResizeObserverError = e.message.includes('ResizeObserver loop');
    if (isResizeObserverError) {
      e.stopImmediatePropagation();
      return false;
    }
  }
});

const debounce = (callback: (...args: any[]) => void, delay: number) => {
  let tid: any;
  return function (...args: any[]) {
    const ctx = window;
    tid && clearTimeout(tid);
    tid = setTimeout(() => {
      callback.apply(ctx, args);
    }, delay);
  };
};

const _ = (window as any).ResizeObserver;
(window as any).ResizeObserver = class ResizeObserver extends _ {
  constructor(callback: any) {
    callback = debounce(callback, 20);
    super(callback);
  }
};

(async () => {
  const queryStringifyOptions = {
    transformSearchString: transformSearchStringJsonSafe,
  };

  const RouteAdapter = ({
    children,
  }: {
    children: (History: { history: any; location: Location }) => React.ReactNode;
  }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const adaptedHistory = React.useMemo(
      () => ({
        replace(location: Location) {
          navigate(location, { replace: true, state: location.state });
        },
        push(location: Location) {
          navigate(location, { replace: false, state: location.state });
        },
      }),
      [navigate]
    );
    return children({ history: adaptedHistory, location });
  };

  const container = document.getElementById('root');
  const root = createRoot(container!); // createRoot(container!) if you use TypeScript

  root.render(
    <BrowserRouter>
      <QueryParamProvider
        // @ts-ignore:
        ReactRouterRoute={RouteAdapter}
        stringifyOptions={queryStringifyOptions}
      >
        <ThemeSelector isDark={false}>
          <App />
        </ThemeSelector>
      </QueryParamProvider>
    </BrowserRouter>
  );
})();

// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
